import {useMultiStyleConfig} from '@chakra-ui/react';
import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';
import {mode} from '@chakra-ui/theme-tools';

import {components as componentsDark, semantic as semanticDark} from '../../design-tokens/dark';
import {components, core, semantic} from '../../design-tokens/light';
import type {MultipartStyles} from '../types';
import {getCoreValueAsThemeToken} from '../types';

import {textareaTheme} from './Textarea';

const anatomy = [`preview`, `input`, `error`, `root`] as const;
export type Parts = (typeof anatomy)[number];

const {defineMultiStyleConfig, definePartsStyle} = createMultiStyleConfigHelpers(anatomy);

const baseStyle = definePartsStyle(props => ({
  root: {
    display: `flex`,
    alignItems: `center`,
    justifyContent: `space-between`,
    px: `space-200`,
    py: `space-150`,
    border: `1px solid`,
    [`&[data-editing=true]`]: {
      borderColor: mode(
        components.colors.components[`form-control`][`stroke-pressed`],
        componentsDark.colors.components[`form-control`][`stroke-pressed`],
      )(props),
    },
    borderColor: mode(
      components.colors.components[`form-control`][`stroke-default`],
      componentsDark.colors.components[`form-control`][`stroke-default`],
    )(props),
    borderRadius: `radius-100`,

    fontSize: `md`,
    gap: `space-100`,
  },
  error: {
    px: getCoreValueAsThemeToken(core, `space.space-150`),
  },
  preview: {
    margin: getCoreValueAsThemeToken(core, `space.space-100`),
    width: `full`,
  },
}));

const variants = {
  text: {
    input: {
      p: `space-100`,
      bg: `white`,
      display: `inline-block`,
    },
  },
  textarea: {
    input: {
      ...textareaTheme.baseStyle,
    },
  },
  number: definePartsStyle(props => ({
    input: {
      order: `-1`,
      bg: `transparent`,
      border: `none`,
      [`&label`]: {
        color: mode(semantic.colors.text.secondary, semanticDark.colors.text.secondary)(props),
      },
    },
  })),
};

export const editableFormControlTheme = defineMultiStyleConfig({
  baseStyle,
  variants,
});

export const useEditableFormControlStyleConfig = (
  props?: Parameters<typeof useMultiStyleConfig>[1],
): MultipartStyles<Parts> => {
  const path = `EditableFormControl`;

  return useMultiStyleConfig(path, props ?? {}) as MultipartStyles<Parts>;
};
